import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPackage, FiCheckCircle, FiCalendar, FiMapPin } = FiIcons;

const Pricing = () => {
  const pricingPlans = [
    {
      name: '4 × 70 Minuten – Kurs',
      subtitle: 'Dein wöchentliches Ruhe-Ritual',
      description:
        'Ideal, wenn du regelmäßig zur Ruhe kommen möchtest und dir einen sanften, beständigen Rhythmus wünschst.',
      price: 169,
      ctaText: 'Neue Kurstermine anfragen',
      features: [
        '4 Sessions à 70 Minuten',
        'Wöchentlicher Rhythmus',
        'Aufbauende Entspannung',
        'Maximal 4 Teilnehmende',
        'Yin Yoga und Klangschalen',
        'Achtsame, professionelle Begleitung',
      ],
    },
    {
      name: '3 Stunden Retreat',
      subtitle: 'Sanfte Auszeit am Wochenende',
      description:
        'Ideal, wenn du dir einen intensiven Raum wünschst, in dem du loslassen, sammeln und neue Kraft spüren kannst.',
      price: 139,
      ctaText: 'Platz sichern - 3 Std. Retreat',
      features: [
        '3 Stunden konzentrierte Ruhe',
        'Wochenend-Ritual',
        'Tiefe Entspannung',
        'Maximal 4 Teilnehmende',
        'Yin Yoga und Klangschalen',
        'Neue Kraft tanken in geschützter Atmosphäre',
      ],
    },
  ];

  const handleContact = () => {
    const element = document.querySelector('#contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="pricing"
      className="py-16 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#F9F5F0' }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-serif text-stone-800 mb-8"
            style={{ fontFamily: 'Libre Baskerville,serif' }}
          >
            Zwei Formate – ein Weg in deine Ruhe
          </h2>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl shadow-lg border max-w-4xl mx-auto mb-10"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#CBB8A9' }}
          >
            <p
              className="text-stone-800 leading-relaxed text-center"
              style={{ fontFamily: 'Lato,sans-serif' }}
            >
              <span
                className="block font-semibold mb-3 text-lg"
                style={{
                  fontFamily: 'Libre Baskerville,serif',
                  color: '#314C40',
                }}
              >
                Teresa, Teilnehmerin:
              </span>
              <span className="italic text-base" style={{ color: '#2C2C2C' }}>
                „Yin und Klang ist eine wundervolle Auszeit vom Alltag. Die
                Gedanken sind ganz bei den Tönen der Klangschalen – und man kann
                gar nicht anders, als loszulassen.“
              </span>
            </p>
          </motion.div>
          <p
            className="text-lg text-stone-600 max-w-3xl mx-auto mb-12"
            style={{ fontFamily: 'Lato,sans-serif' }}
          >
            Wähle das Format, das zu deinem Alltag passt.
            <br />
            Beide führen dich Schritt für Schritt in tiefere Entspannung und
            innere Klarheit.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-xl border relative overflow-hidden flex flex-col"
              style={{ borderColor: '#CBB8A9' }}
            >
              <div className="text-center mb-8">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm"
                  style={{ backgroundColor: '#CBB8A9' }}
                >
                  <SafeIcon
                    icon={FiPackage}
                    className="w-8 h-8"
                    style={{ color: '#314C40' }}
                  />
                </div>
                <h3
                  className="text-2xl font-serif text-stone-800 mb-2"
                  style={{ fontFamily: 'Libre Baskerville,serif' }}
                >
                  {plan.name}
                </h3>
                <p
                  className="text-lg font-semibold mb-4"
                  style={{ fontFamily: 'Lato,sans-serif', color: '#b45309' }}
                >
                  {plan.subtitle}
                </p>
                <div className="flex items-center justify-center mb-4">
                  <span
                    className="text-4xl font-bold text-stone-800"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    {plan.price} €
                  </span>
                  <span
                    className="text-lg text-stone-600 ml-2"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    brutto
                  </span>
                </div>
              </div>
              <p
                className="text-stone-600 leading-relaxed mb-6 text-center"
                style={{ fontFamily: 'Lato,sans-serif' }}
              >
                {plan.description}
              </p>
              <div className="bg-stone-50 rounded-xl p-6 mb-8 flex-grow">
                <h4
                  className="text-lg font-semibold text-stone-800 mb-4 text-center"
                  style={{ fontFamily: 'Lato,sans-serif' }}
                >
                  Das ist enthalten:
                </h4>
                <div className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: featureIndex * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center"
                    >
                      <SafeIcon
                        icon={FiCheckCircle}
                        className="w-5 h-5 mr-3 flex-shrink-0"
                        style={{ color: '#314C40' }}
                      />
                      <span
                        className="text-stone-700 text-sm"
                        style={{ fontFamily: 'Lato,sans-serif' }}
                      >
                        {feature}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <motion.button
                onClick={handleContact}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full py-4 px-6 rounded-xl font-semibold transition-colors duration-200 shadow-lg flex items-center justify-center space-x-2 text-white mt-auto"
                style={{
                  fontFamily: 'Lato,sans-serif',
                  backgroundColor: '#314C40',
                }}
              >
                <span>{plan.ctaText}</span>
                <SafeIcon icon={FiIcons.FiArrowRight} className="w-5 h-5" />
              </motion.button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-white/80 backdrop-blur-sm p-8 md:p-12 rounded-2xl shadow-lg border text-center"
          style={{ borderColor: '#CBB8A9' }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-8 shadow-sm"
            style={{ backgroundColor: '#CBB8A9' }}
          >
            <SafeIcon
              icon={FiCalendar}
              className="w-8 h-8"
              style={{ color: '#314C40' }}
            />
          </div>
          <h3
            className="text-2xl font-serif text-stone-800 mb-10"
            style={{ fontFamily: 'Libre Baskerville,serif' }}
          >
            Wo und wann?
          </h3>
          <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto text-left">
            <div className="bg-stone-50 p-8 rounded-xl border border-stone-100 flex flex-col shadow-sm">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center mr-4">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-stone-700" />
                </div>
                <h4
                  className="text-xl font-bold text-stone-800"
                  style={{ fontFamily: 'Libre Baskerville,serif' }}
                >
                  4 × 70 Minuten - Kurs
                </h4>
              </div>
              <div className="space-y-6 flex-grow">
                <div>
                  <p
                    className="font-bold text-stone-800 mb-2 uppercase text-xs tracking-wider"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Veranstaltungsort:
                  </p>
                  <p
                    className="text-stone-600 leading-relaxed"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Fairyflorals - Flowers &amp; Flow
                    <br />
                    Wolfhager Straße 409
                    <br />
                    34128 Kassel
                  </p>
                </div>
                <div>
                  <p
                    className="font-bold text-stone-800 mb-2 uppercase text-xs tracking-wider"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Termine &amp; Uhrzeit:
                  </p>
                  <p
                    className="text-stone-600 leading-relaxed"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    jeweils Dienstag, 18:30 Uhr bis 19:40 Uhr
                    <br />
                    03. November 2026
                    <br />
                    10. November 2026
                    <br />
                    17. November 2026
                    <br />
                    24. November 2026
                  </p>
                  <p
                    className="text-sm italic text-stone-500 mt-4 border-t border-stone-200 pt-4"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Frage den Start einer neuen Reihe bitte über unser
                    Anmeldeformular an.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-stone-50 p-8 rounded-xl border border-stone-100 flex flex-col shadow-sm">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center mr-4">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-stone-700" />
                </div>
                <h4
                  className="text-xl font-bold text-stone-800"
                  style={{ fontFamily: 'Libre Baskerville,serif' }}
                >
                  3 Stunden Retreat
                </h4>
              </div>
              <div className="space-y-6 flex-grow">
                <div>
                  <p
                    className="font-bold text-stone-800 mb-2 uppercase text-xs tracking-wider"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Veranstaltungsorte:
                  </p>
                  <p
                    className="text-stone-600 leading-relaxed"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    VITAL CENTER by Alex
                    <br />
                    Sophienstraße 32 - 3. Etage
                    <br />
                    34117 Kassel
                  </p>
                  <p
                    className="text-stone-600 leading-relaxed mt-4"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Fairyflorals - Flowers &amp; Flow
                    <br />
                    Wolfhager Straße 409
                    <br />
                    34128 Kassel
                  </p>
                </div>
                <div>
                  <p
                    className="font-bold text-stone-800 mb-2 uppercase text-xs tracking-wider"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Termine &amp; Uhrzeit:
                  </p>
                  <p
                    className="text-stone-600 leading-relaxed"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    jeweils 14:00 Uhr bis 17:00 Uhr
                    <span className="block font-medium mt-2 text-stone-800">
                      Samstag, 14. November 2026
                    </span>
                    <span className="block font-medium mt-1 text-stone-800">
                      Sonntag, 13. Dezember 2026
                    </span>
                    <span className="block font-medium mt-1 text-stone-800">
                      Samstag, 16. Januar 2027
                    </span>
                    <span className="block font-medium mt-1 text-stone-800">
                      Sonntag, 21. Februar 2027
                    </span>
                    <span className="block text-sm text-stone-500 mt-3">
                      Zukünftig: weitere Retreat Termine werden laufend bekannt
                      gegeben.
                    </span>
                  </p>
                  <p
                    className="text-sm italic text-stone-500 mt-4 border-t border-stone-200 pt-4"
                    style={{ fontFamily: 'Lato,sans-serif' }}
                  >
                    Frage weitere Termine bitte über unser Anmeldeformular an.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;